import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export interface Operation {
  id: string;
  type: 'bulk-submit' | 'bulk-download' | 'import' | 'export' | 'sync' | 'generate';
  label: string;
  status: 'running' | 'completed' | 'failed';
  progress: { current: number; total: number };
  startedAt: Date;
  completedAt?: Date;
  result?: { success: number; failed: number; errors: { identifier: string; error: string }[] };
  currentItem?: string;
}

interface NotificationItem {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  operationId?: string;
}

interface OperationsContextValue {
  operations: Operation[];
  notifications: NotificationItem[];
  unreadCount: number;
  startOperation: (op: Omit<Operation, 'id' | 'startedAt' | 'status'>) => string;
  updateProgress: (id: string, current: number, currentItem?: string) => void;
  completeOperation: (id: string, result?: Operation['result']) => void;
  failOperation: (id: string, error: string) => void;
  addNotification: (n: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => void;
  markAllRead: () => void;
  clearNotifications: () => void;
  getActiveOperations: () => Operation[];
}

const OperationsContext = createContext<OperationsContextValue | null>(null);

export const useOperations = () => {
  const ctx = useContext(OperationsContext);
  if (!ctx) throw new Error('useOperations must be inside OperationsProvider');
  return ctx;
};

export const OperationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const idCounter = useRef(0);

  const genId = () => `op-${Date.now()}-${++idCounter.current}`;

  const startOperation = useCallback((op: Omit<Operation, 'id' | 'startedAt' | 'status'>) => {
    const id = genId();
    const newOp: Operation = { ...op, id, status: 'running', startedAt: new Date() };
    setOperations((prev) => [newOp, ...prev]);
    return id;
  }, []);

  const updateProgress = useCallback((id: string, current: number, currentItem?: string) => {
    setOperations((prev) =>
      prev.map((op) =>
        op.id === id
          ? { ...op, progress: { ...op.progress, current }, currentItem }
          : op
      )
    );
  }, []);

  const addNotificationInternal = useCallback((n: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => {
    setNotifications((prev) => [
      { ...n, id: genId(), timestamp: new Date(), read: false },
      ...prev.slice(0, 49),
    ]);
  }, []);

  const completeOperation = useCallback((id: string, result?: Operation['result']) => {
    setOperations((prev) =>
      prev.map((op) =>
        op.id === id
          ? { ...op, status: 'completed', completedAt: new Date(), result }
          : op
      )
    );
    setOperations((prev) => {
      const op = prev.find((o) => o.id === id);
      if (op) {
        const hasErrors = result && result.failed > 0;
        addNotificationInternal({
          type: hasErrors ? 'warning' : 'success',
          title: op.label,
          message: result
            ? `${result.success} udanych, ${result.failed} błędów z ${result.success + result.failed}`
            : 'Operacja zakończona',
          operationId: id,
        });
      }
      return prev;
    });
  }, [addNotificationInternal]);

  const failOperation = useCallback((id: string, error: string) => {
    setOperations((prev) =>
      prev.map((op) =>
        op.id === id
          ? { ...op, status: 'failed', completedAt: new Date() }
          : op
      )
    );
    setOperations((prev) => {
      const op = prev.find((o) => o.id === id);
      if (op) {
        addNotificationInternal({
          type: 'error',
          title: op.label,
          message: error,
          operationId: id,
        });
      }
      return prev;
    });
  }, [addNotificationInternal]);

  const addNotification = useCallback((n: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => {
    addNotificationInternal(n);
  }, [addNotificationInternal]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getActiveOperations = useCallback(() => {
    return operations.filter((op) => op.status === 'running');
  }, [operations]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <OperationsContext.Provider
      value={{
        operations,
        notifications,
        unreadCount,
        startOperation,
        updateProgress,
        completeOperation,
        failOperation,
        addNotification,
        markAllRead,
        clearNotifications,
        getActiveOperations,
      }}
    >
      {children}
    </OperationsContext.Provider>
  );
};
